// AuthLayout.js — Casca das páginas /login e /criar-conta.
//
// Layout de duas colunas (o padrão de mercado — Notion, Figma, Shopify):
//   esquerda: painel da marca (só no desktop), com a proposta de valor;
//   direita:  o formulário, numa coluna estreita e centrada.
//
// ================= A PAREDE DE CAPAS (15/09/2026) =================
//
// O painel da esquerda era três linhas de texto e uma lista de três itens: o
// dono olhou e disse, com razão, que estava "feio e simples demais". O problema
// não era a falta de enfeite — era a tela não parecer de UMA EDITORA. Qualquer
// serviço do mundo podia ter aquela coluna.
//
// Agora o fundo do painel é o acervo de verdade: as capas publicadas, vindas da
// mesma rota que a home usa, empilhadas em colunas inclinadas. É o argumento
// que só esta empresa tem, e ele chega antes de qualquer frase.
//
// TRÊS CUIDADOS QUE O DESENHO EXIGE:
//  1. O VÉU. Texto sobre capa colorida não se lê. Um gradiente da cor do fundo
//     cobre a parede, forte do lado do texto e fraco na diagonal oposta, onde
//     as capas continuam visíveis.
//  2. SE NÃO HOUVER CAPAS, NÃO HÁ PAREDE. Sem rede, com a API fora do ar ou com
//     acervo pequeno, o painel volta a ser só texto — em vez de uma moldura
//     vazia anunciando que algo quebrou. É o mesmo critério da VitrineCapas.
//  3. AS CAPAS NÃO SÃO CONTEÚDO. `aria-hidden` e `alt=""`: são textura. Quem
//     usa leitor de tela não ganha nada com dezoito nomes de livro lidos antes
//     do formulário, e perde tempo até chegar no campo de e-mail.
//
// No celular a parede não cabe ao lado de nada, então ela vira uma FAIXA fina
// acima do formulário — a mesma ideia, na altura que sobra.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BorderGlow from "../ui/BorderGlow";
import logo from "../../assets/imgs/logo.png";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

/** Abaixo disto a parede fica repetitiva e denuncia o truque. */
const MINIMO_DE_CAPAS = 9;
const COLUNAS = 3;

const Vantagem = ({ icone, titulo, texto }) => (
  <li className="flex gap-3">
    <i className={"bi " + icone + " text-insigne-orange text-lg mt-0.5 shrink-0"} />
    <div>
      <p className="font-semibold text-white text-sm">{titulo}</p>
      <p className="text-white/55 text-sm leading-relaxed">{texto}</p>
    </div>
  </li>
);

/** As capas do acervo, para o fundo do painel e para a faixa do celular. */
const useCapas = () => {
  const [capas, setCapas] = useState([]);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const r = await fetch(API_URL + "ebooks-recentes");
        if (!r.ok) return;
        const dados = await r.json();
        if (!ativo || !Array.isArray(dados)) return;
        setCapas(
          dados
            .filter((l) => l?.image)
            .slice(0, 18)
            .map((l) => `${API_URL}public/${l.image}`)
        );
      } catch {
        /* sem capas → o painel vira só texto */
      }
    })();
    return () => { ativo = false; };
  }, []);

  return capas;
};

const AuthLayout = ({ titulo, subtitulo, children, rodape }) => {
  const capas = useCapas();
  const temParede = capas.length >= MINIMO_DE_CAPAS;

  // Distribui as capas em colunas, na ordem: coluna 0 fica com 0, 3, 6...
  const colunas = Array.from({ length: COLUNAS }, (_, c) =>
    capas.filter((_, i) => i % COLUNAS === c)
  );

  return (
    <div className="bg-insigne-bg fundo-abertura min-h-screen pt-[var(--nav-h)] text-white">
      <div className="max-w-[1180px] mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_minmax(0,440px)] gap-10 lg:gap-16 items-center">
          {/* ===== Painel da marca (desktop) ===== */}
          <aside
            className="relative hidden lg:flex min-h-[600px] flex-col justify-end overflow-hidden
                       rounded-painel border-2 border-insigne-sombra bg-insigne-navy-deep p-10"
          >
            {temParede && (
              // `scale-125` com `-rotate-6`: girar um retângulo deixa cantos
              // vazios: a escala os empurra para fora da área recortada.
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 flex justify-center gap-3 -rotate-6 scale-125"
              >
                {colunas.map((coluna, c) => (
                  <div
                    key={c}
                    className={"flex flex-col gap-3 " + (c % 2 === 1 ? "-mt-16" : "-mt-4")}
                  >
                    {coluna.map((src, i) => (
                      <img
                        key={src + i}
                        src={src}
                        alt=""
                        loading="lazy"
                        className="w-[132px] aspect-[3/4] object-cover rounded-selo
                                   border border-insigne-sombra shadow-[3px_3px_0_0_rgb(var(--c-sombra))]"
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* O véu: opaco onde mora o texto, transparente na diagonal oposta. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0
                         bg-gradient-to-tr from-insigne-navy-deep via-insigne-navy-deep/95 to-insigne-navy-deep/45"
            />

            <div className="relative">
              <img src={logo} alt="Editora Insigne Acadêmica" className="h-11 w-auto mb-7" />
              <h2 className="titulo-display text-[3rem]">
                O acervo inteiro,
                <br />
                na sua conta.
              </h2>
              <p className="text-white/65 leading-relaxed mt-3 mb-7 max-w-md">
                Sua conta Insigne guarda os e-books que você comprou, acompanha seus
                pedidos e reúne suas InsigneCoins em um só lugar.
              </p>
              <ul className="space-y-4 max-w-md">
                <Vantagem
                  icone="bi-collection-play"
                  titulo="Biblioteca sempre disponível"
                  texto="Leia o que você comprou em qualquer aparelho, quando quiser."
                />
                <Vantagem
                  icone="bi-bag-check"
                  titulo="Pedidos acompanhados de perto"
                  texto="Veja o andamento das compras e o rastreio dos livros impressos."
                />
                <Vantagem
                  icone="bi-coin"
                  titulo="InsigneCoins que viram desconto"
                  texto="Você ganha a cada compra e a cada livro avaliado."
                />
              </ul>
            </div>
          </aside>

          {/* ===== Formulário ===== */}
          <main className="w-full max-w-[440px] mx-auto lg:mx-0">
            {/* A faixa de capas do celular. Mesma ideia da parede, deitada, e
                com as pontas esmaecidas para ela não terminar num corte seco. */}
            {temParede && (
              <div
                aria-hidden="true"
                className="lg:hidden -mx-4 mb-7 overflow-hidden
                           [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]"
              >
                <div className="flex gap-2.5 px-4">
                  {capas.slice(0, 10).map((src, i) => (
                    <img
                      key={src + i}
                      src={src}
                      alt=""
                      loading="lazy"
                      className="h-24 w-auto shrink-0 rounded-selo border border-insigne-sombra
                                 shadow-[2px_2px_0_0_rgb(var(--c-sombra))]"
                    />
                  ))}
                </div>
              </div>
            )}

            <Link to={main} className="lg:hidden inline-block mb-7">
              <img src={logo} alt="Editora Insigne Acadêmica" className="h-10 w-auto" />
            </Link>

            {/* Um cartão único por página — o caso do BorderGlow. Em toque a
                borda simplesmente não acende (App.css, `@media (hover: none)`),
                sem deixar buraco. Os CAMPOS continuam sem animação de entrada:
                campo que ainda está se movendo não pode receber o foco de quem
                chega com o gerenciador de senhas pronto. */}
            <BorderGlow raio={4} className="cartao p-6 md:p-8">
              {/* O traço laranja acima do título: é o mesmo sinal que o site usa
                  para dizer "aqui começa o que importa", e sem ele o cartão
                  abria com um texto solto no canto. */}
              <span aria-hidden="true" className="block h-1 w-10 rounded-selo bg-insigne-orange mb-4" />
              <h1 className="titulo-display mb-0 text-[2.5rem] md:text-[2.9rem]">{titulo}</h1>
              {subtitulo && <p className="text-white/60 text-sm mt-2">{subtitulo}</p>}
              <div className="mt-6">{children}</div>
            </BorderGlow>

            {rodape && <div className="text-center text-sm text-white/60 mt-6">{rodape}</div>}

            {/* Saída para quem caiu aqui sem querer — a navbar some nesta página
                no celular, e sem este link a única volta era o botão do
                navegador. */}
            <div className="text-center mt-4">
              <Link to={main} className="text-xs text-white/40 hover:text-white/70 transition-colors">
                <i className="bi bi-arrow-left" /> Voltar para a loja
              </Link>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
